import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download } from 'lucide-react';

import ReportFiltersBar from '@/components/reports/ReportFiltersBar';
import SalesReport from '@/components/reports/SalesReport';
import CollectionsReport from '@/components/reports/CollectionsReport';
import CustomersReport from '@/components/reports/CustomersReport';
import ProductsReport from '@/components/reports/ProductsReport';
import VisitsReport from '@/components/reports/VisitsReport';

const REPORT_TYPES = [
  { key: 'sales',       label: 'المبيعات',   icon: '📈', color: 'bg-blue-50 border-blue-200 text-blue-700' },
  { key: 'collections', label: 'التحصيلات', icon: '💰', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  { key: 'customers',   label: 'العملاء',   icon: '👥', color: 'bg-purple-50 border-purple-200 text-purple-700' },
  { key: 'products',    label: 'المنتجات',  icon: '📦', color: 'bg-amber-50 border-amber-200 text-amber-700' },
  { key: 'visits',      label: 'الزيارات',  icon: '📍', color: 'bg-pink-50 border-pink-200 text-pink-700' },
];

function getDefaultDates() {
  const now = new Date(Date.now() + 2 * 60 * 60 * 1000);
  const today = now.toISOString().split('T')[0];
  const monthStart = today.slice(0, 7) + '-01';
  return { from: monthStart, to: today };
}

export default function Reports() {
  const defaults = getDefaultDates();
  const [activeReport, setActiveReport] = useState('sales');
  const [dateFrom, setDateFrom] = useState(defaults.from);
  const [dateTo, setDateTo] = useState(defaults.to);

  const handleDateChange = (from, to) => {
    setDateFrom(from);
    setDateTo(to);
  };

  const activeType = REPORT_TYPES.find(r => r.key === activeReport);

  return (
    <div className="flex flex-col min-h-full" dir="rtl">
      {/* ── Sticky header ── */}
      <div className="sticky top-0 z-10 bg-background border-b border-border shadow-sm">
        {/* Title bar */}
        <div className="px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-bold text-foreground">التقارير والتحليلات</h1>
          <span className="text-xs text-muted-foreground bg-muted rounded-full px-2.5 py-1 font-medium">
            {dateFrom} → {dateTo}
          </span>
        </div>

        {/* Report type selector */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar px-4 pb-3">
          {REPORT_TYPES.map(r => (
            <button key={r.key} onClick={() => setActiveReport(r.key)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl border-2 transition-all font-semibold text-sm ${
                activeReport === r.key
                  ? r.color + ' border-current shadow-sm'
                  : 'bg-card border-border text-muted-foreground hover:border-border/80'
              }`}>
              <span className="text-base">{r.icon}</span>
              {r.label}
            </button>
          ))}
        </div>

        {/* Date range filter */}
        <ReportFiltersBar dateFrom={dateFrom} dateTo={dateTo} onDateChange={handleDateChange} />
      </div>

      {/* ── Report content ── */}
      <div className="flex-1 overflow-y-auto p-4">
        <AnimatePresence mode="wait">
          <motion.div key={activeReport} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            {activeReport === 'sales' && <SalesReport dateFrom={dateFrom} dateTo={dateTo} />}
            {activeReport === 'collections' && <CollectionsReport dateFrom={dateFrom} dateTo={dateTo} />}
            {activeReport === 'customers' && <CustomersReport dateFrom={dateFrom} dateTo={dateTo} />}
            {activeReport === 'products' && <ProductsReport dateFrom={dateFrom} dateTo={dateTo} />}
            {activeReport === 'visits' && <VisitsReport dateFrom={dateFrom} dateTo={dateTo} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}